/* eslint-disable import/no-anonymous-default-export */
import axios from 'axios';
import cookies from 'react-cookies';
import signV4 from '@omc/api-signer/es/signV4_H5';
import ampHandler, { getRequestData, getResponseData, getErrorCode, getErrorData } from "@/utils/apm";
import globalData from '@/utils/globalData';
import { uuidv4 } from '@/utils/sign/util';
import { client } from "@/common/common";
import getkey from '../../utils/sign/signKey';

const message = {
  warning: (msg: any) => {
    console.log('[WARN]', msg)
  },
  error: (msg: any) => {
    console.error('[ERROR]', msg)
  },
}

const genBaseHeader = (originHeaders: Record<string, any>) => {
  const uuId = uuidv4() || ""
  let base: Record<string, any> = {
    "ct": originHeaders?.ct || 10,
    "language": originHeaders?.language || globalData.getLanguage(),
    "v": originHeaders?.v || globalData.getVersion(),
    'sv': 'v4',
    'st': Math.round(Date.now() / 1000) + '',
    'nonce': (Date.now() + '') + (Math.round(Math.random() * 999999)),
    "x-b3-traceid": uuId,
    "x-b3-spanid": uuId.substring(0, 16),
    "request-timestamp": Date.now(),
  }
  const sid = cookies.load('sid');
  if (sid) base = {...base, sid };
  const token = cookies.load('token');
  if (token) base = {...base, token };
  if (originHeaders) {
    base = {...base, ...originHeaders};
  }
  return base;
}

const getRequestOptions = (config: any) => {
  const { url, method, headers, data, params, baseURL } = config || {};

  const transData = {
    ...params,
    ...data,
  };

  return {
    url: baseURL && url && url.indexOf(baseURL) === -1 ? `${baseURL}${url}` : (url || ''),
    method,
    data: JSON.stringify(transData) === '{}' ? '' : transData,
    header: {
      language: headers.language || '',
      ct: headers.ct || 10,
      v: headers.v || globalData.version,
      token: headers.token || '',
      sid: headers.sid || '',
      sv: headers.sv || '',
    },
  };
};

const transAxiosParams = (data: any) => {
  const transData = data ? {} : data
  if (!data) {
    return transData
  }
  for (const key in data) {
    if (Object.prototype.hasOwnProperty.call(data, key)) {
      if ((data[key] === undefined || data[key] === null)) {
        continue;
      }
      transData[key] = data[key]
    }
  }
  return transData
}

// v4签名header
const setV4Header = (config: any) => {
  try {
    config.headers = genBaseHeader(config.headers);
    config.data = transAxiosParams(config.data);
    config.params = transAxiosParams(config.params);
    const preOptions = getRequestOptions(config);
    const options = signV4(preOptions, getkey);
    config.headers = {
      ...config.headers,
      ...options.header,
    };
    // axios 请求 header/params/data 需与 v4签名用于加签的 header/params/data 保持一致
    return config;
  } catch (error) {
    console.log('[app setV4Header error]', error);
    ampHandler.uploadEventInfo("setV4Header", Date.now(), error ? error.toString() : "setV4Header error");
  }
}

export default (options: Record<string, any> = {}) => {
  // 创建axios实例
  const service = axios.create({
    // baseURL: process.env.REACT_APP_BASE_API, // api的base_url
  });
  service.interceptors.request.use(
    (config) => {
      const tid = cookies.load('tid');
      if (tid) config.headers['tid'] = tid;
      if (
        (config && config.url && config.url.indexOf("/bff/collector/collect/request") > -1) ||
        (config && config.url && config.url.indexOf("/bff/collector/collect/event") > -1)
      ) {
        // 请求日志&事件上报日志
        options.header = {
          "content-type": "application/x-protobuf" // 默认值
        }
        return config;
      }
      // v4加签-云控
      const v4Config = setV4Header(config);
      ampHandler.uploadEventInfo("apiSignV4", Date.now(), "apiSignV4");
      return v4Config;
    },
    (error) => {
      return Promise.reject(error);
    }
  );

  // response拦截器
  service.interceptors.response.use(
    (response) => {
      if (!~options?.url?.indexOf('/collector/')) {
        try {
          ampHandler.uploadPerformanceInfo(getRequestData(response.config), getResponseData(response))
        } catch (err) {
          console.error('[apm uploadPerformanceInfo]', err);
        }
      }
      if (options.afterResponse) options.afterResponse(response.data);
      let result = response.data;
      if (response.data.requestLogs && response.data.response) {
        result = response.data.response;
        result.logs = response.data.requestLogs;
      }
      if (response?.config?.headers?.sid && Number(result.code) === 401) {
        if (client.isInApp()) {
          const errorData = {
            code: result.code,
            message: result?.message || '',
          };
          client.connectWebViewJavascriptBridge((bridge:any) => {
            bridge.callHandler('onBffError', JSON.stringify(errorData));
          });
        }
      }
      return Promise.resolve(result);
    },
    (error) => {
      const errorInfo = {
        success: false,
        code: getErrorCode(error),
      };
      if (!~options?.url?.indexOf('/collector/')) {
        try {
          ampHandler.uploadPerformanceInfo(getRequestData(error?.config), getErrorData(error, errorInfo));
        } catch (err) {
          console.error('[apm uploadPerformanceInfo]', err);
        }
      }
      ampHandler.uploadEventInfo("mobMainRequestError", Date.now(), error ? error.toString() : "apiRequestError");
      if (error.toString() !== 'Cancel') {
        message.warning(error.message || '系统异常！');
      }

      return Promise.reject(error);
    }
  );
  return service;
}
